// AP PDP JS — color/size variant resolution, gallery swap, size sheet.
// Idempotent per .ap-pdp and re-inits on shopify:section:load so the theme editor stays live.
(function () {
  // Every AP Product section includes this script, so a landing page with five of them loads
  // it five times. Only the first copy runs; it boots every section (and re-boots on
  // shopify:section:load), so later copies would only bind duplicate page-level listeners.
  if (window.__apPdpLoaded) return;
  window.__apPdpLoaded = true;

  // Bad JSON disables only the affected feature instead of throwing and killing the whole PDP.
  function safeParse(node, fallback) {
    try { return JSON.parse((node && node.textContent) || ''); } catch (e) { return fallback; }
  }

  // Currency-correct: prefer the server-rendered formatted price (data-ap-prices, built with
  // the shop's money format) so multi-currency / Markets render correctly; fall back to a
  // simple USD format only if the map is missing.
  function fmtPrice(prices, id, cents) {
    if (prices && prices[id] != null) return prices[id];
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  // Tracking (ap-cards.js apTrack → Shopify custom events); absent apTrack is a no-op.
  function track(name, data) {
    if (typeof window.apTrack === 'function') window.apTrack(name, data);
  }

  function el(tag, attrs) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    return n;
  }

  function initPdp(root) {
    if (root.dataset.apPdpInit) return;
    root.dataset.apPdpInit = '1';

    // ── Data ──────────────────────────────────────────────────────────────────
    var variants = safeParse(root.querySelector('[data-ap-variants]'), []);
    var media = safeParse(root.querySelector('[data-ap-media]'), {});
    var prices = safeParse(root.querySelector('[data-ap-prices]'), {});
    var comparePrices = safeParse(root.querySelector('[data-ap-compare-prices]'), {});
    // Pack-contents grid only: per-variant "Save $x (y%)" in the pack row, server-formatted.
    var packSaveMap = safeParse(root.querySelector('[data-ap-pack-save-map]'), {});

    // ── State / DOM refs ──────────────────────────────────────────────────────
    var state = { color: null, size: null, qty: 1 };
    var addBtns = root.querySelectorAll('[data-ap-add-to-bag]');
    var err = root.querySelector('[data-ap-error]');
    var mainEl = root.querySelector('[data-ap-main]');
    var thumbsEl = root.querySelector('[data-ap-thumbs]');
    var colorBtns = root.querySelectorAll('[data-ap-color]');
    var hasColor = colorBtns.length > 0;
    var sizeBtns = root.querySelectorAll('[data-ap-size]');
    // Size sheet: with no size chosen an add button opens the sheet, whose buttons drive
    // the in-page size buttons (one selection path, one state).
    var sheet = root.querySelector('[data-ap-sizesheet]');
    if (sheet && typeof sheet.showModal !== 'function') sheet = null;
    var sheetSizeBtns = sheet ? sheet.querySelectorAll('[data-ap-sheet-size]') : [];
    // Pack tiers (singles): one pair / 3-pack / 5-pack radios. The selected tier decides what the
    // add button adds; Mixed (one of each color) is the 3-pack tier's mixed product.
    var tierEls = root.querySelectorAll('[data-ap-tier]');
    var tiers = {};
    tierEls.forEach(function (t) {
      var key = t.getAttribute('data-ap-tier');
      tiers[key] = {
        el: t,
        input: t.querySelector('input'),
        key: key,
        name: t.getAttribute('data-ap-tier-name'),
        price: t.getAttribute('data-ap-tier-price'),
        skus: safeParse(t.querySelector('[data-ap-tier-skus]'), []),
        mixedSkus: safeParse(t.querySelector('[data-ap-tier-mixed-skus]'), [])
      };
    });
    var checkedTier = root.querySelector('[data-ap-tier] input:checked');
    state.tier = checkedTier ? checkedTier.value : '1';
    state.mixed = false;
    var tieredBtn = root.querySelector('[data-ap-add-tiered]');
    var mixedBtn = root.querySelector('[data-ap-mixed]');
    var mixedNote = root.querySelector('[data-ap-mixed-note]');

    // ── Helpers ───────────────────────────────────────────────────────────────
    // Position-independent: match by whether the chosen color/size values appear in the
    // variant's option set, rather than assuming option1=Color, option2=Size.
    function variantFor(color, size) {
      return variants.find(function (v) {
        var opts = (v.options && v.options.length) ? v.options : [v.option1, v.option2, v.option3];
        var matchColor = (color == null) || opts.indexOf(color) !== -1;
        var matchSize = (size == null) || opts.indexOf(size) !== -1;
        return matchColor && matchSize;
      });
    }

    // Struck-through compare-at price + "% OFF" for discounted variants (packs); hidden otherwise.
    var compareWraps = root.querySelectorAll('[data-ap-compare-wrap]');
    var compareEls = root.querySelectorAll('[data-ap-compare]');
    var saleLabel = root.querySelector('[data-ap-sale-label]');
    var saveEl = root.querySelector('[data-ap-save]');
    function renderCompare(v) {
      var onSale = v.compare_at_price != null && v.compare_at_price > v.price;
      compareWraps.forEach(function (w) { w.hidden = !onSale; });
      if (saleLabel) saleLabel.hidden = !onSale;
      if (saveEl) saveEl.hidden = !onSale;
      if (!onSale) return;
      compareEls.forEach(function (e) { e.textContent = fmtPrice(comparePrices, v.id, v.compare_at_price); });
      if (saveEl) saveEl.textContent = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '% OFF';
    }

    // ── Variant refresh ───────────────────────────────────────────────────────
    // A variant is resolvable once size is chosen AND (a color is chosen OR the
    // product has no Color option — e.g. the Size-only mixed packs).
    // The pack the selected tier adds (null for one pair), in the shape addPack and the size sheet use.
    function currentPack() {
      var t = tiers[state.tier];
      if (!t || state.tier === '1') return null;
      if (state.mixed) return { btn: tieredBtn, skus: t.mixedSkus, name: '3-pack-mixed', label: 'mixed 3-pack', mixed: true };
      return { btn: tieredBtn, skus: t.skus, name: t.name, label: t.name, mixed: false };
    }
    // What a size resolves to for the current selection: the pack variant, or the single's.
    function skuFor(size) {
      var pk = currentPack();
      return pk ? packVariant(pk, size) : variantFor(state.color, size);
    }

    // "ADD 3-PACK · BLACK / M — $122": names exactly what the tap adds.
    function renderTieredBtn() {
      if (!tieredBtn) return;
      var pk = currentPack();
      var name = pk ? 'ADD ' + pk.label.toUpperCase() : 'ADD TO BAG';
      var detail = [];
      if (hasColor && state.color && !state.mixed) detail.push(state.color.toUpperCase());
      if (state.size) detail.push(state.size);
      var price = tiers[state.tier] ? tiers[state.tier].price : '';
      var sku = state.size ? skuFor(state.size) : null;
      if (pk) {
        var anySku = sku || pk.skus[0];
        if (anySku && anySku.price) price = anySku.price;
      } else if (sku) {
        price = fmtPrice(prices, sku.id, sku.price);
      }
      var labelEl = tieredBtn.querySelector('[data-ap-add-label]');
      var priceEl = tieredBtn.querySelector('[data-ap-add-price]');
      if (labelEl) labelEl.textContent = name + (detail.length ? ' · ' + detail.join(' / ') : '');
      if (priceEl) priceEl.textContent = price;
      if (pk) tieredBtn.disabled = !!(sku && !sku.available);
    }

    function refresh() {
      var colorReady = !hasColor || state.color != null || state.mixed;
      sizeBtns.forEach(function (b) {
        var s = b.getAttribute('data-ap-size');
        if (!colorReady) { b.disabled = false; return; }
        var sv = skuFor(s);
        b.disabled = !(sv && sv.available);
      });
      // Sheet sizes mirror the in-page buttons' availability for the current color.
      sheetSizeBtns.forEach(function (b) {
        var twin = sizeBtnFor(b.getAttribute('data-ap-sheet-size'));
        b.disabled = !twin || twin.disabled;
      });
      var v = (colorReady && state.size) ? variantFor(state.color, state.size) : null;
      if (v) {
        addBtns.forEach(function (btn) {
          btn.setAttribute('data-ap-variant-id', v.id);
          btn.disabled = !v.available;
        });
        root.querySelectorAll('[data-ap-price]').forEach(function (e2) {
          e2.textContent = fmtPrice(prices, v.id, v.price);
        });
        renderCompare(v);
        var packSaveEl = root.querySelector('[data-ap-pack-save]');
        if (packSaveEl && packSaveMap[v.id] != null) {
          packSaveEl.textContent = packSaveMap[v.id];
          packSaveEl.hidden = !packSaveMap[v.id];
        }
      } else {
        addBtns.forEach(function (btn) {
          btn.removeAttribute('data-ap-variant-id');
          btn.disabled = false;
        });
      }
      renderTieredBtn();
    }

    function sizeBtnFor(size) {
      for (var i = 0; i < sizeBtns.length; i++) {
        if (sizeBtns[i].getAttribute('data-ap-size') === size) return sizeBtns[i];
      }
      return null;
    }

    // ── Gallery (built as DOM nodes — no innerHTML interpolation) ──────────────
    // `sizes` lives on the main slot in Liquid so JS-rendered images match the server LCP <img>.
    var mainSizes = mainEl ? mainEl.getAttribute('data-ap-sizes') : null;
    var videoFirst = !root.hasAttribute('data-landing') || root.hasAttribute('data-landing-primary');
    function renderMain(item) {
      if (!mainEl) return;
      if (item.type === 'image') {
        // Keep the server-rendered LCP <img> when it is already this image: replacing it would
        // flash and re-request the hero.
        var cur = mainEl.firstElementChild;
        if (cur && cur.tagName === 'IMG' && cur.getAttribute('src') === item.src) {
          cur.alt = item.alt || '';
          return;
        }
      }
      mainEl.textContent = '';
      if (item.type === 'video') {
        // Created only when shown (slot 0 on load, or on selection), so a clip in a later slot
        // downloads only when the shopper asks for it.
        var vid = el('video', { 'data-ap-mainvideo': '', class: 'ap-pdp__media', src: item.src, muted: '', loop: '', playsinline: '', autoplay: '', preload: 'metadata' });
        if (item.poster) vid.setAttribute('poster', item.poster);
        mainEl.appendChild(vid);
        vid.muted = true; vid.playsInline = true; vid.play().catch(function () {});
      } else {
        var img = el('img', { class: 'ap-pdp__media', src: item.src, alt: item.alt || '' });
        if (item.srcset) img.setAttribute('srcset', item.srcset);
        if (item.srcset && mainSizes) img.setAttribute('sizes', mainSizes);
        mainEl.appendChild(img);
      }
    }

    // Pack-contents grid (The Essential Pack): one captioned 3:4 tile per style in the pack,
    // replacing main image + thumbnails. Mirrors the server-rendered markup in ap-product.liquid.
    function renderGrid(tiles) {
      mainEl.textContent = '';
      tiles.forEach(function (t) {
        var fig = el('figure', { class: 'ap-pdp__tile' });
        if (t.src) {
          var img = el('img', { class: 'ap-pdp__tile-img', src: t.src, alt: t.alt || '', width: '600', height: '800' });
          if (t.srcset) img.setAttribute('srcset', t.srcset);
          if (t.sizes) img.setAttribute('sizes', t.sizes);
          fig.appendChild(img);
        }
        var cap = el('figcaption', { class: 'ap-pdp__tile-cap' });
        var name = el('span', { class: 'ap-pdp__tile-name' });
        name.textContent = t.name || '';
        cap.appendChild(name);
        if (t.coverage) {
          var cov = el('span', { class: 'ap-pdp__tile-coverage' });
          cov.textContent = t.coverage;
          cap.appendChild(cov);
        }
        fig.appendChild(cap);
        mainEl.appendChild(fig);
      });
    }

    // One selection path for thumbs and swipes, so the two can never disagree.
    var gallery = { items: [], index: 0 };
    function showItem(i) {
      if (i < 0 || i >= gallery.items.length) return;
      gallery.index = i;
      if (thumbsEl) thumbsEl.querySelectorAll('[data-ap-thumb]').forEach(function (x, j) { x.classList.toggle('is-active', j === i); });
      renderMain(gallery.items[i]);
    }
    // Horizontal swipe on the main image steps one item (touch-action: pan-y in CSS keeps
    // vertical scrolling native). Ignored in the pack grid, which has no item sequence.
    if (mainEl) {
      var swipeX = null, swipeY = 0;
      mainEl.addEventListener('pointerdown', function (e) { swipeX = e.clientX; swipeY = e.clientY; });
      mainEl.addEventListener('pointercancel', function () { swipeX = null; });
      mainEl.addEventListener('pointerup', function (e) {
        if (swipeX == null) return;
        var dx = e.clientX - swipeX, dy = e.clientY - swipeY;
        swipeX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        showItem(gallery.index + (dx < 0 ? 1 : -1));
      });
    }

    root.apSwapGallery = function (color) {
      var setColor = color;
      var set = media[color];
      if (!set) {
        setColor = Object.keys(media)[0];
        set = setColor ? media[setColor] : null;
      }
      if (!set) return;

      var isGrid = !!(set.grid && set.grid.length && mainEl);
      if (mainEl) mainEl.classList.toggle('ap-pdp__main--grid', isGrid);
      if (thumbsEl) thumbsEl.hidden = isGrid;
      if (isGrid) {
        // The initial color's grid is server-rendered (with LCP hints); don't rebuild it.
        if (mainEl.getAttribute('data-ap-grid-color') !== setColor) {
          renderGrid(set.grid);
          mainEl.setAttribute('data-ap-grid-color', setColor);
        }
        if (thumbsEl) thumbsEl.textContent = '';
        gallery.items = [];
        return;
      }
      if (mainEl) mainEl.removeAttribute('data-ap-grid-color');

      // Images are {src, srcset, thumb}; a bare URL string is accepted too.
      var images = (set.images || []).map(function (im) { return typeof im === 'string' ? { src: im } : im; });
      var altText = set.alt || '';
      var items = images.map(function (im) {
        return { type: 'image', src: im.src, srcset: im.srcset || '', thumb: im.thumb || im.src, alt: altText };
      });
      // Video first on the product page and the first product on a landing page: it replaces
      // the server LCP <img> on load, with that photo as its poster (same URL, so no refetch).
      // Later landing sections keep photo first, video second, so the clips below the fold only
      // download when the shopper selects them.
      if (set.video) {
        var first = images[0];
        var videoSlot = (videoFirst || !items.length) ? 0 : 1;
        items.splice(videoSlot, 0, { type: 'video', src: set.video, poster: first ? first.src : '', thumb: first ? (first.thumb || first.src) : '', alt: altText });
      }
      if (items.length === 0) return;

      gallery.items = items;
      gallery.index = 0;
      var imgNo = 0;
      var labels = items.map(function (it) { return it.type === 'video' ? 'Video' : 'Image ' + (++imgNo); });
      if (thumbsEl) {
        thumbsEl.textContent = '';
        items.forEach(function (it, i) {
          var btn = el('button', { type: 'button', 'data-ap-thumb': '', 'data-i': String(i), class: 'ap-pdp__thumb', 'aria-label': labels[i] });
          if (it.type === 'video') {
            var badge = el('span', { class: 'ap-pdp__playbadge', 'aria-hidden': 'true' });
            // An SVG, not a ▶ character: iOS Safari drew the character as a missing-glyph "?" box.
            badge.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" focusable="false"><path d="M7 4.5v15l12.5-7.5z" fill="currentColor"/></svg>';
            btn.appendChild(badge);
          }
          if (it.thumb) btn.appendChild(el('img', { src: it.thumb, alt: '', loading: 'lazy' }));
          btn.addEventListener('click', function () { showItem(i); });
          thumbsEl.appendChild(btn);
        });
      }
      showItem(0);
    };

    // ── Outgoing links keep the ad's UTM parameters so attribution survives the extra hop:
    // pack links (pointed at the pack in the selected color) and, on landing pages, the rating
    // link to the product page's reviews. URL-based so a #hash stays after the query. ──
    function utmParams() {
      var utm = [];
      new URLSearchParams(location.search).forEach(function (v, k) { if (/^utm_/.test(k)) utm.push([k, v]); });
      return utm;
    }
    function withParams(href, params) {
      var u = new URL(href, location.origin);
      params.forEach(function (kv) { u.searchParams.append(kv[0], kv[1]); });
      return (u.origin === location.origin ? '' : u.origin) + u.pathname + u.search + u.hash;
    }
    var packLinks = root.querySelectorAll('[data-ap-pack-link]');
    function updatePackLinks() {
      var utm = utmParams();
      packLinks.forEach(function (a) {
        var byColor = {};
        try { byColor = JSON.parse(a.getAttribute('data-ap-pack-variants') || '{}'); } catch (e) {}
        var params = [];
        if (state.color && byColor[state.color]) params.push(['variant', byColor[state.color]]);
        var base = a.getAttribute('data-ap-pack-url') || a.getAttribute('href').split('?')[0];
        a.setAttribute('href', withParams(base, params.concat(utm)));
      });
    }
    root.querySelectorAll('[data-ap-utm-link]').forEach(function (a) {
      a.setAttribute('href', withParams(a.getAttribute('href'), utmParams()));
    });

    // ── Color selectors (color/size are independent) ──────────────────────────
    // A chosen size that the new selection doesn't stock is dropped, so the add button asks for a
    // size (sheet) instead of turning gray with no explanation.
    function dropUnavailableSize() {
      if (!state.size) return;
      var keep = skuFor(state.size);
      if (keep && keep.available) return;
      state.size = null;
      sizeBtns.forEach(function (x) {
        x.classList.remove('is-active');
        x.setAttribute('aria-pressed', 'false');
      });
    }
    function setMixed(on) {
      state.mixed = on;
      if (mixedBtn) {
        mixedBtn.classList.toggle('is-active', on);
        mixedBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      if (mixedNote) mixedNote.hidden = !on;
    }

    colorBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        setMixed(false);
        state.color = b.getAttribute('data-ap-color');
        colorBtns.forEach(function (x) {
          x.classList.toggle('is-active', x === b);
          x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
        });
        var lbl = root.querySelector('[data-ap-colorlabel]');
        if (lbl) lbl.textContent = state.color;
        dropUnavailableSize();
        // Dynamic FIT accordion: show only the selected color's chunk
        root.querySelectorAll('[data-ap-fit-color]').forEach(function (el) {
          el.hidden = el.getAttribute('data-ap-fit-color') !== state.color;
        });
        root.apSwapGallery(state.color);
        updatePackLinks();
        refresh();
      });
    });

    // ── Pack tiers + Mixed swatch ─────────────────────────────────────────────
    function selectTier(key) {
      var t = tiers[key];
      if (!t || t.input.disabled) return;
      state.tier = key;
      t.input.checked = true;
      tierEls.forEach(function (x) { x.classList.toggle('is-selected', x === t.el); });
      // Mixed exists only as a 3-pack: leaving the 3-pack restores the last single color.
      if (key !== '3' && state.mixed) {
        setMixed(false);
        var back = null;
        colorBtns.forEach(function (x) { if (x.getAttribute('data-ap-color') === state.color) back = x; });
        if (back) { back.click(); return; }
      }
      dropUnavailableSize();
      refresh();
    }
    tierEls.forEach(function (t) {
      t.querySelector('input').addEventListener('change', function () { selectTier(t.getAttribute('data-ap-tier')); });
    });
    if (mixedBtn) {
      mixedBtn.addEventListener('click', function () {
        if (!tiers['3'] || tiers['3'].input.disabled) return;
        // state.color keeps the last real color: the gallery and FIT text stay on it.
        setMixed(true);
        colorBtns.forEach(function (x) {
          x.classList.remove('is-active');
          x.setAttribute('aria-pressed', 'false');
        });
        var lbl = root.querySelector('[data-ap-colorlabel]');
        if (lbl) lbl.textContent = 'Mixed (one of each)';
        selectTier('3');
      });
    }

    // ── Size selectors ────────────────────────────────────────────────────────
    sizeBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled) return;
        state.size = b.getAttribute('data-ap-size');
        sizeBtns.forEach(function (x) {
          x.classList.toggle('is-active', x === b);
          x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
        });
        if (err) err.hidden = true;
        refresh();
      });
    });

    // ── Size guide dialog ─────────────────────────────────────────────────────
    var sgDialog = root.querySelector('[data-ap-sizeguide]');
    // Openers: the in-page "Size guide" link and the one inside the size sheet.
    var sgOpens = root.querySelectorAll('[data-ap-sizeguide-open]');
    if (sgDialog && sgOpens.length && typeof sgDialog.showModal === 'function') {
      // From inside the size sheet the guide replaces the sheet (two stacked modals on a phone
      // screen are confusing), and closing the guide brings the sheet back.
      var reopenSheet = false;
      sgOpens.forEach(function (o) {
        o.addEventListener('click', function () {
          if (sheet && sheet.open && sheet.contains(o)) { reopenSheet = true; sheet.close(); }
          sgDialog.showModal();
        });
      });
      sgDialog.addEventListener('close', function () {
        // Reopen for the same thing (single or pack) that the sheet was opened for.
        if (reopenSheet) { reopenSheet = false; openSheet(sheetPack); }
      });
      sgDialog.querySelector('[data-ap-sizeguide-close]').addEventListener('click', function () { sgDialog.close(); });
      sgDialog.addEventListener('click', function (e) {
        // The inner wrapper covers the whole dialog box, so a click landing on
        // the dialog element itself can only be the ::backdrop.
        if (e.target === sgDialog) sgDialog.close();
      });
    }

    // ── Quantity ──────────────────────────────────────────────────────────────
    root.querySelectorAll('[data-ap-qty]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.qty = Math.max(1, state.qty + (b.getAttribute('data-ap-qty') === '+' ? 1 : -1));
        var qv = root.querySelector('[data-ap-qtyval]');
        if (qv) qv.textContent = state.qty;
      });
    });

    // ── Add to bag (all add buttons) ──────────────────────────────────────────
    var SIZE_MSG = err ? err.textContent : '';
    var adding = false;
    addBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (adding) return;
        // A pack tier adds that pack (same path, events and sheet as the old pack rows).
        var pk = btn === tieredBtn ? currentPack() : null;
        if (pk) {
          track('ap_add_tap', { source: 'pack', has_size: !!state.size, pack: pk.name });
          if (!state.size) {
            if (sheet) { openSheet(pk); return; }
            if (err) { err.textContent = SIZE_MSG; err.hidden = false; }
            return;
          }
          addPack(pk, state.size, 'pack');
          return;
        }
        track('ap_add_tap', { source: 'inpage', has_size: !!state.size, variant: btn.getAttribute('data-ap-variant-id') || '' });
        if (!state.size) {
          // ct09: no size → the size sheet, never a "select a size" error (when the sheet exists).
          if (sheet) { openSheet(null); return; }
          if (err) { err.textContent = SIZE_MSG; err.hidden = false; }
          return;
        }
        addToBag(btn.getAttribute('data-ap-variant-id'), 'inpage');
      });
    });

    // Shared by the add button and the size sheet.
    function showAddError(msg) {
      if (!err) return;
      err.textContent = msg;
      err.hidden = false;
    }
    async function addToBag(id, source) {
      if (adding) return;
      if (!id || !window.apAddToCart) {
        // Unreachable in normal flows, but a dead click must never be mute.
        showAddError('Sorry — could not add to bag. Please try again.');
        return;
      }
      adding = true;
      if (err) err.hidden = true;
      addBtns.forEach(function (b) { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      var res = await window.apAddToCart(id, state.qty, { source: source });
      adding = false;
      addBtns.forEach(function (b) { b.removeAttribute('aria-busy'); });
      // Re-derive the buttons from the current selection: the shopper may have changed color or
      // size while the add was in flight, and the next add must use that selection.
      refresh();
      if (!(res && res.ok)) {
        showAddError((res && res.error) ? res.error : 'Sorry — could not add to bag. Please try again.');
      }
    }

    // ── Pack add (singles' pack tiers): adds that pack in the color + size chosen on this page
    // (the mixed 3-pack has no Color option). Its own add path so the single's add data keeps
    // pointing at the single's variant.
    function packVariant(pack, size) {
      return pack.skus.find(function (v) {
        var o = v.options || [];
        return (!hasColor || pack.mixed || o.indexOf(state.color) !== -1) && o.indexOf(size) !== -1;
      });
    }
    // `size` is passed in (not read from state) so a pack added from the sheet in a size the single
    // doesn't stock never leaves a half-set selection behind.
    async function addPack(pack, size, source) {
      if (adding) return;
      var pv = packVariant(pack, size);
      if (!pv || !pv.available) {
        if (err) { err.textContent = 'That size is sold out in this pack.'; err.hidden = false; }
        return;
      }
      if (!window.apAddToCart) { showAddError('Sorry — could not add to bag. Please try again.'); return; }
      adding = true;
      if (err) err.hidden = true;
      pack.btn.disabled = true; pack.btn.setAttribute('aria-busy', 'true');
      // One pack per tap: the tier is the quantity.
      var res = await window.apAddToCart(String(pv.id), 1, { source: source });
      adding = false;
      pack.btn.disabled = false; pack.btn.removeAttribute('aria-busy');
      // Re-derive the button from the current selection, which may have changed mid-add.
      refresh();
      if (!(res && res.ok) && err) {
        err.textContent = (res && res.error) ? res.error : 'Sorry — could not add to bag. Please try again.';
        err.hidden = false;
      }
    }

    // ── Size sheet (any add tapped with no size) ─────────────────────────────
    // The sheet adds whatever opened it: the single (pack null) or that pack. Sizes reflect the
    // availability of the thing being added; errors go back where the shopper tapped.
    var sheetPack = null;
    var sheetHelp = sheet ? sheet.querySelector('[data-ap-sheet-help]') : null;
    var SHEET_HELP = sheetHelp ? sheetHelp.textContent : '';
    function openSheet(pack) {
      sheetPack = pack || null;
      if (sheetHelp) sheetHelp.textContent = sheetPack ? 'Tap your size to add the ' + (sheetPack.label || sheetPack.name) + ' to your bag.' : SHEET_HELP;
      sheetSizeBtns.forEach(function (b) {
        var size = b.getAttribute('data-ap-sheet-size');
        if (sheetPack) {
          var pv = packVariant(sheetPack, size);
          b.disabled = !(pv && pv.available);
        } else {
          var twin = sizeBtnFor(size);
          b.disabled = !twin || twin.disabled;
        }
      });
      var colorEl = sheet.querySelector('[data-ap-sheet-color]');
      if (colorEl) colorEl.textContent = (sheetPack && sheetPack.mixed) ? ' — Mixed' : ((hasColor && state.color) ? ' — ' + state.color : '');
      if (err) err.hidden = true;
      sheet.showModal();
    }
    if (sheet) {
      var sheetClose = sheet.querySelector('[data-ap-sizesheet-close]');
      if (sheetClose) sheetClose.addEventListener('click', function () { sheet.close(); });
      sheet.addEventListener('click', function (e) {
        // The inner wrapper fills the dialog box, so only a ::backdrop click targets the dialog.
        if (e.target === sheet) sheet.close();
      });
      sheetSizeBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.disabled || adding) return;
          var size = b.getAttribute('data-ap-sheet-size');
          var twin = sizeBtnFor(size);
          if (!twin) return;
          track('ap_add_tap', { source: 'sheet', has_size: true, size: size, pack: sheetPack ? sheetPack.name : '' });
          if (!twin.disabled) twin.click(); // same selection path as a direct in-page tap
          if (sheetPack) {
            // A pack can be in stock in a size the single isn't; add it in that size either way.
            sheet.close();
            addPack(sheetPack, size, 'sheet');
            return;
          }
          if (state.size !== size) return;
          sheet.close();
          var v = variantFor(state.color, state.size);
          addToBag(v && v.available ? String(v.id) : null, 'sheet');
        });
      });
    }

    // Modal dialogs restored from the back/forward cache would still block the page.
    window.addEventListener('pagehide', function () {
      if (sheet && sheet.open) sheet.close();
      var guide = root.querySelector('[data-ap-sizeguide]');
      if (guide && guide.open) guide.close();
    });

    // ── Initial selection — every entry path converges here ──────────────────
    // A valid ?variant= (lineup / portrait-wall links) chooses WHICH color to
    // preselect; otherwise it's the first color. Selection happens through the
    // same .click() path a user takes, so visual state and logical state cannot
    // diverge. Size is intentionally never preselected: the ?variant id is just
    // Liquid encoding a color (it always points at that color's first size), and
    // shoppers must choose their size explicitly.
    var initColorBtn = colorBtns.length ? colorBtns[0] : null;
    // Landing pages declare their opening color (data-default-color, only rendered in landing
    // mode and only when it matches a Color value); ?variant= below still wins.
    var declared = root.hasAttribute('data-landing') ? root.getAttribute('data-default-color') : null;
    if (declared) {
      for (var di = 0; di < colorBtns.length; di++) {
        if (colorBtns[di].getAttribute('data-ap-color') === declared) { initColorBtn = colorBtns[di]; break; }
      }
    }
    var pv = new URLSearchParams(location.search).get('variant');
    if (pv) {
      var preV = variants.find(function (x) { return String(x.id) === pv; });
      if (preV) {
        var preOpts = (preV.options && preV.options.length) ? preV.options : [preV.option1, preV.option2, preV.option3];
        for (var ci = 0; ci < colorBtns.length; ci++) {
          if (preOpts.indexOf(colorBtns[ci].getAttribute('data-ap-color')) !== -1) { initColorBtn = colorBtns[ci]; break; }
        }
      }
    }
    if (initColorBtn) {
      initColorBtn.click(); // sets state.color + label + fit + gallery, then refresh()
    } else {
      // No Color option (Size-only mixed packs): default gallery, then resolve state.
      var fallbackColor = Object.keys(media)[0];
      if (fallbackColor) root.apSwapGallery(fallbackColor);
      refresh();
    }
  }

  function boot() {
    // Only the first product section on a landing page may be the primary one (h1, eager LCP
    // image); in the theme editor, any extra section marked primary shows a warning.
    var primaries = document.querySelectorAll('.ap-pdp[data-landing-primary]');
    if (window.Shopify && window.Shopify.designMode) {
      for (var i = 1; i < primaries.length; i++) {
        var r = primaries[i];
        if (r.querySelector('.ap-pdp__dm-warning')) continue;
        var w = el('p', { class: 'ap-pdp__dm-warning' });
        w.textContent = 'AP Product: more than one product section on this page is the primary landing section. Turn "Primary landing section" off on all but the first.';
        r.insertBefore(w, r.firstChild);
      }
    }
    document.querySelectorAll('.ap-pdp').forEach(initPdp);
  }

  if (document.readyState !== 'loading') boot(); else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
